import React from "react";
import { toast, Slide as toastTransitionSlide } from "react-toastify";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  addEmailOrPhoneToWaitList,
  FORCE_CORPORATE_EMAILS,
} from "../services/waitList";
import { EMAIL_REGEX } from "./constants";
import { contactUs } from "../services/auth";

const validateEmail = (email) => {
  return EMAIL_REGEX.test(email);
};

/**
 * @type {import('react-toastify').ToastOptions}
 */
const TOAST_OPTIONS = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "light",
  transition: toastTransitionSlide,
  icon: ({ type }) => (
    <div
      style={{
        width: "24px",
        height: "24px",
        background: type === "error" ? "#ff6030" : "#1d1d1d",
        color: "#ffffff",
        borderRadius: "4px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0,
      }}
    >
      {type === "error" ? "!" : "✓"}
    </div>
  ),
  style: {
    borderRadius: "4px",
    padding: "12px 16px",
    boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
    color: "rgba(29, 29, 29, 0.85)",
    backgroundColor: "#ffffff",
    border: "none",
  },
};

const addToWaitList = async ({ email, phoneNumber }, landingId, onSuccess) => {
  if (!email && !phoneNumber) {
    return;
  }

  if (email) {
    let invalidEmailMessage;

    if (!validateEmail(email)) {
      invalidEmailMessage = "Invalid email address";
    } else if (
      (email.toString().toLowerCase().endsWith("gmail.com") ||
        email.toString().toLowerCase().endsWith("yahoo.com") ||
        email.toString().toLowerCase().endsWith("hotmail.com") ||
        email.toString().toLowerCase().endsWith("outlook.com") ||
        email.toString().toLowerCase().endsWith("mail.com") ||
        email.toString().toLowerCase().endsWith("icloud.com")) &&
      FORCE_CORPORATE_EMAILS
    ) {
      invalidEmailMessage = "Please use your company email address";
    }

    if (invalidEmailMessage) {
      toast.error(invalidEmailMessage, TOAST_OPTIONS);
      return;
    }
  } else {
    // Phone number should already be normalized to international format
    // But validate it's in the right format (starts with + and has enough digits)
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    if (!phoneNumber.startsWith("+") || digitsOnly.length < 10) {
      toast.error("Invalid phone number", TOAST_OPTIONS);
      return;
    }
    // Optional: validate with isValidPhoneNumber if you want strict validation
    // For now, we'll just check format to allow test numbers
    if (!isValidPhoneNumber(phoneNumber)) {
      // Only show error if it's clearly malformed, not just an invalid pattern
      if (digitsOnly.length < 10 || digitsOnly.length > 15) {
        toast.error("Invalid phone number", TOAST_OPTIONS);
        return;
      }
    }
  }

  const response = await addEmailOrPhoneToWaitList(
    { email, phoneNumber },
    landingId
  );

  if (!response) {
    return;
  }

  if (typeof response === "string") {
    toast.error(response, TOAST_OPTIONS);
    return;
  }

  if (!response.success) {
    toast.error(response.message, TOAST_OPTIONS);
    return;
  }

  if (onSuccess) {
    onSuccess();
    return;
  }

  if (response.recordsCount) {
    toast.success(
      `Welcome to the future of political communication! You're number ${response.recordsCount} on the list.`,
      TOAST_OPTIONS
    );
  }
};

const requestDemo = async ({ name, email, phoneNumber, reason }, onSuccess) => {
  if (!email && !phoneNumber) {
    return;
  }

  if (email && !validateEmail(email)) {
    toast.error("Invalid email address", TOAST_OPTIONS);
    return;
  }

  if (phoneNumber) {
    // Validate format (less strict - allow test numbers)
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      toast.error("Invalid phone number", TOAST_OPTIONS);
      return;
    }
    // Only validate with isValidPhoneNumber if it's clearly malformed
    // This allows test numbers like 222-222-2222
    if (!phoneNumber.startsWith("+") && digitsOnly.length < 10) {
      toast.error("Invalid phone number", TOAST_OPTIONS);
      return;
    }
  }

  if (!reason) {
    reason = "Request a Demo";
  }

  const response = await contactUs({
    name,
    reason,
    email,
    phoneNumber,
    details: `ReachThePeople.ai - ${reason}`,
  });

  if (!response) {
    return;
  }

  if (typeof response === "string") {
    toast.error(response, TOAST_OPTIONS);
    return;
  }

  if (!response.success) {
    toast.error(response.message, TOAST_OPTIONS);
    return;
  }

  if (onSuccess) {
    onSuccess();
    return;
  }

  toast.success(
    "Thank you for requesting a demo! We'll be in touch soon.",
    TOAST_OPTIONS
  );
};

const requestCallback = async ({ phoneNumber }, onSuccess) => {
  if (!phoneNumber) {
    return;
  }

  // Normalize phone number
  const digitsOnly = phoneNumber.replace(/\D/g, "");
  let normalizedPhone = phoneNumber;

  if (!phoneNumber.startsWith("+")) {
    if (digitsOnly.length === 10) {
      normalizedPhone = `+1${digitsOnly}`;
    } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
      normalizedPhone = `+${digitsOnly}`;
    }
  }

  const response = await contactUs({
    reason: "Request Callback",
    phoneNumber: normalizedPhone,
    details: "ReachThePeople.ai - Request Callback",
  });

  if (!response) {
    return;
  }

  if (typeof response === "string") {
    toast.error(response, TOAST_OPTIONS);
    return;
  }

  if (!response.success) {
    toast.error(response.message, TOAST_OPTIONS);
    return;
  }

  if (onSuccess) {
    onSuccess();
    return;
  }

  toast.success(
    "Thank you for requesting a callback! We'll be in touch soon.",
    TOAST_OPTIONS
  );
};

export {
  TOAST_OPTIONS,
  addToWaitList,
  requestDemo,
  requestCallback,
  validateEmail,
};
